import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Form, Alert } from 'react-bootstrap';
import { CompanySideBar } from '../../components/CompanySideBar';
import { TagSelect } from '../../components/TagSelect';
import { NumericFormat } from 'react-number-format';
import { getAllSelectors } from '../../services/SelectorsService';
import { getVacancyById, createVacancy, updateVacancy } from '../../services/VacanciesService';
import { getCitiesByCountry } from '../../services/SelectorsService';
import { useAuthStore } from '../../store';
import JoditEditor from 'jodit-react';

export function CompanyPostJobs() {
    const history = useHistory();
    const location = useLocation();
    const { auth } = useAuthStore();

    const [formData, setFormData] = useState({
        titulo: '',
        emailContacto: '',
        tags: [],
        descripcion: '',
        inglesRequerido: false,
        modalidadTrabajoId: '',
        tipoTrabajoId: '',
        tipoContratoTrabajoId: '',
        tiempoExperienciaRequeridoId: '',
        categoriaId: '',
        salarioMinimo: '',
        salarioMaximo: '',
        paisId: '',
        ciudadId: '',
        direccion: '',
        tiempoVigencia: '',
        limiteAplicantes: ''
    });

    const [selectors, setSelectors] = useState({
        categorias: [],
        modalidadesTrabajo: [],
        tiposTrabajo: [],
        tiposContrato: [],
        tiemposExperiencia: [],
        paises: [],
        ciudades: []
    });
    const [availableCities, setAvailableCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSelectors, setLoadingSelectors] = useState(true);
    const [loadingVacancy, setLoadingVacancy] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [vacancyId, setVacancyId] = useState(null);
    const editorRef = useRef(null);
    const [descripcionContent, setDescripcionContent] = useState('');

    const joditConfig = useMemo(() => ({
        height: 300,
        language: 'es',
        toolbar: true,
        spellcheck: true,
        buttons: [
            'bold', 'italic', 'underline', 'strikethrough',
            '|', 'ul', 'ol', '|',
            'outdent', 'indent', '|',
            'font', 'fontsize', 'brush', 'paragraph', '|',
            'image', 'link', '|',
            'align', 'undo', 'redo', '|',
            'hr', 'eraser', 'copyformat', '|',
            'fullsize', 'selectall', 'print', '|',
            'source'
        ]
    }), []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const id = queryParams.get('id');
        if (id) {
            setIsEditMode(true);
            setVacancyId(id);
        }
    }, [location.search]);

    useEffect(() => {
        if (!isEditMode && auth?.user?.email) {
            setFormData(prev => ({
                ...prev,
                emailContacto: auth.user.email
            }));
        }
    }, [isEditMode, auth?.user?.email]);

    useEffect(() => {
        const loadSelectors = async () => {
            try {
                setLoadingSelectors(true);
                const response = await getAllSelectors();
                const data = response.data;
                setSelectors({
                    categorias: data.categorias || [],
                    modalidadesTrabajo: data.modalidadesTrabajo || [],
                    tiposTrabajo: data.tiposTrabajo || [],
                    tiposContrato: data.tiposContrato || [],
                    tiemposExperiencia: data.tiemposExperiencia || [],
                    paises: data.paises || [],
                    ciudades: data.ciudades || []
                });
            } catch (err) {
                setError('Error al cargar los selectores. Por favor, recarga la página.');
                console.error('Error loading selectors:', err);
            } finally {
                setLoadingSelectors(false);
            }
        };

        loadSelectors();
    }, []);

    useEffect(() => {
        const loadVacancy = async () => {
            if (!isEditMode || !vacancyId) return;

            try {
                setLoadingVacancy(true);
                const response = await getVacancyById(vacancyId);
                const vacancy = response.data;

                // Convertir tags del backend al formato que espera TagSelect
                const convertedTags = Array.isArray(vacancy.tags)
                    ? vacancy.tags.map(tag => {
                        // Si el tag ya viene como string
                        if (typeof tag === 'string') {
                            return {
                                id: tag,
                                nombre: tag,
                                label: tag,
                                value: tag,
                            };
                        }

                        // Si el tag viene con la estructura { tag: { id, nombre } }
                        if (tag && tag.tag && tag.tag.nombre) {
                            return {
                                id: tag.tag.id || tag.tag.nombre,
                                nombre: tag.tag.nombre,
                                label: tag.tag.nombre,
                                value: tag.tag.nombre,
                            };
                        }

                        // Si el tag viene como objeto plano { id, nombre } u otras variantes
                        const nombre = tag && (tag.nombre || tag.label || tag.value) || '';
                        const id = tag && (tag.id || tag.value || nombre) || nombre;
                        return {
                            id,
                            nombre,
                            label: nombre,
                            value: nombre,
                        };
                    })
                    : [];

                const descripcionValue = vacancy.descripcion || '';
                setFormData({
                    titulo: vacancy.titulo || '',
                    emailContacto: vacancy.emailContacto || '',
                    tags: convertedTags,
                    descripcion: descripcionValue,
                    inglesRequerido: vacancy.inglesRequerido || false,
                    modalidadTrabajoId: vacancy.modalidadTrabajoId || '',
                    tipoTrabajoId: vacancy.tipoTrabajoId || '',
                    tipoContratoTrabajoId: vacancy.tipoContratoTrabajoId || '',
                    tiempoExperienciaRequeridoId: vacancy.tiempoExperienciaRequeridoId || '',
                    categoriaId: vacancy.categoriaId || '',
                    salarioMinimo: vacancy.salarioMinimo || '',
                    salarioMaximo: vacancy.salarioMaximo || '',
                    paisId: vacancy.paisId || '',
                    ciudadId: vacancy.ciudadId || '',
                    direccion: vacancy.direccion || '',
                    tiempoVigencia: vacancy.tiempoVigencia || '',
                    limiteAplicantes: vacancy.limiteAplicantes != null ? String(vacancy.limiteAplicantes) : ''
                });
                setDescripcionContent(descripcionValue);

                if (vacancy.paisId) {
                    loadCitiesForCountry(vacancy.paisId);
                }
            } catch (err) {
                setError('Error al cargar la vacante. Por favor, intenta nuevamente.');
                console.error('Error loading vacancy:', err);
            } finally {
                setLoadingVacancy(false);
            }
        };

        loadVacancy();
    }, [isEditMode, vacancyId]);

    const loadCitiesForCountry = async (paisId) => {
        if (!paisId) {
            setAvailableCities([]);
            setFormData(prev => ({ ...prev, ciudadId: '' }));
            return;
        }

        try {
            const response = await getCitiesByCountry(paisId);
            setAvailableCities(response.data || []);
        } catch (err) {
            console.error('Error loading cities:', err);
            setAvailableCities([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNumericChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value ? parseFloat(value.replace(/,/g, '')) : ''
        }));
    };

    const handleCountryChange = (e) => {
        const paisId = e.target.value;
        setFormData(prev => ({ ...prev, paisId, ciudadId: '' }));
        loadCitiesForCountry(paisId);
    };

    const validateForm = () => {
        if (!formData.titulo.trim()) {
            setError('El título es requerido');
            return false;
        }
        if (!formData.emailContacto.trim()) {
            setError('El email de contacto es requerido');
            return false;
        }
        if (!formData.modalidadTrabajoId) {
            setError('La modalidad de trabajo es requerida');
            return false;
        }
        if (!formData.tipoTrabajoId) {
            setError('El tipo de trabajo es requerido');
            return false;
        }
        if (!formData.tipoContratoTrabajoId) {
            setError('El tipo de contrato es requerido');
            return false;
        }
        if (!formData.tiempoExperienciaRequeridoId) {
            setError('El tiempo de experiencia es requerido');
            return false;
        }
        if (!formData.categoriaId) {
            setError('La categoría es requerida');
            return false;
        }
        if (!formData.paisId) {
            setError('El país es requerido');
            return false;
        }
        if (!formData.ciudadId) {
            setError('La ciudad es requerida');
            return false;
        }
        if (!formData.direccion.trim()) {
            setError('La dirección es requerida');
            return false;
        }
        if (formData.salarioMinimo && formData.salarioMaximo) {
            if (parseFloat(formData.salarioMinimo) > parseFloat(formData.salarioMaximo)) {
                setError('El salario mínimo no puede ser mayor que el salario máximo');
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        setFormData(prev => ({
            ...prev,
            descripcion: descripcionContent
        }));

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            const tagsNames = formData.tags.map(tag => tag.nombre);

            const payload = {
                titulo: formData.titulo.trim(),
                emailContacto: formData.emailContacto.trim(),
                tags: tagsNames,
                descripcion: descripcionContent || '',
                inglesRequerido: formData.inglesRequerido,
                modalidadTrabajoId: formData.modalidadTrabajoId,
                tipoTrabajoId: formData.tipoTrabajoId,
                tipoContratoTrabajoId: formData.tipoContratoTrabajoId,
                tiempoExperienciaRequeridoId: formData.tiempoExperienciaRequeridoId,
                categoriaId: formData.categoriaId,
                salarioMinimo: formData.salarioMinimo ? parseFloat(formData.salarioMinimo) : null,
                salarioMaximo: formData.salarioMaximo ? parseFloat(formData.salarioMaximo) : null,
                paisId: formData.paisId,
                ciudadId: formData.ciudadId,
                direccion: formData.direccion.trim(),
                tiempoVigencia: formData.tiempoVigencia ? parseInt(formData.tiempoVigencia) : null,
                limiteAplicantes: formData.limiteAplicantes ? parseInt(formData.limiteAplicantes) : null
            };

            if (isEditMode) {
                await updateVacancy(vacancyId, payload);
                setSuccess(true);
                setTimeout(() => {
                    history.push('/company-manage-job');
                }, 1500);
            } else {
                await createVacancy(payload);
                setSuccess(true);
                setTimeout(() => {
                    history.push('/company-manage-job');
                }, 1500);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Error al guardar la vacante. Por favor, intenta nuevamente.';
            setError(errorMessage);
            console.error('Error saving vacancy:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loadingSelectors) {
        return (
            <div className="page-content bg-white">
                <div className="content-block">
                    <div className="section-full bg-white p-t50 p-b20">
                        <div className="container">
                            <div className="row">
                                <CompanySideBar />
                                <div className="col-xl-9 col-lg-8 m-b30">
                                    <div className="text-center p-t50 p-b50">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Cargando...</span>
                                        </div>
                                        <p className="m-t20">Cargando formulario...</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loadingVacancy) {
        return (
            <div className="page-content bg-white">
                <div className="content-block">
                    <div className="section-full bg-white p-t50 p-b20">
                        <div className="container">
                            <div className="row">
                                <CompanySideBar />
                                <div className="col-xl-9 col-lg-8 m-b30">
                                    <div className="text-center p-t50 p-b50">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Cargando...</span>
                                        </div>
                                        <p className="m-t20">Cargando datos de la vacante...</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="page-content bg-white">
                <div className="content-block">
                    <div className="section-full bg-white p-t50 p-b20">
                        <div className="container">
                            <div className="row">
                                <CompanySideBar />
                                <div className="col-xl-9 col-lg-8 m-b30">
                                    <div className="job-bx submit-resume">
                                        <div className="job-bx-title clearfix">
                                            <h5 className="font-weight-700 pull-left text-uppercase">
                                                {isEditMode ? 'Editar Vacante' : 'Publicar una Vacante'}
                                            </h5>
                                            <Link to={"/company-manage-job"} className="site-button right-arrow button-sm float-right">Back</Link>
                                        </div>

                                        {error && (
                                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                                {error}
                                            </Alert>
                                        )}

                                        {success && (
                                            <Alert variant="success">
                                                {isEditMode ? 'Vacante actualizada exitosamente. Redirigiendo...' : 'Vacante creada exitosamente. Redirigiendo...'}
                                            </Alert>
                                        )}

                                        <form onSubmit={handleSubmit}>
                                            <div className="row">
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Titulo *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Ingresa el titulo de la vacante"
                                                            name="titulo"
                                                            value={formData.titulo}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Email de Contacto *</label>
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            placeholder="info@gmail.com"
                                                            name="emailContacto"
                                                            value={formData.emailContacto}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-8 col-md-8">
                                                    <div className="form-group">
                                                        <label>Tags</label>
                                                        <TagSelect
                                                            value={formData.tags}
                                                            onChange={(selectedTags) => {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    tags: selectedTags
                                                                }));
                                                            }}
                                                            tipo="vacante"
                                                            placeholder="Selecciona o crea tags..."
                                                        />
                                                        {/* Mostrar tags seleccionados como badges similar a KeySkills en Resume */}
                                                        {formData.tags && formData.tags.length > 0 && (
                                                            <div className="job-time mr-auto mt-2">
                                                                {formData.tags.map((tag, index) => {
                                                                    const tagName = tag.nombre || tag.label || tag.value || tag;
                                                                    return (
                                                                        <Link key={index} to={"#"} className="mr-1">
                                                                            <span>{tagName}</span>
                                                                        </Link>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className='d-flex align-items-center col-lg-4 col-md-4'>
                                                    <div className="custom-control custom-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            id="check1"
                                                            name="inglesRequerido"
                                                            checked={formData.inglesRequerido}
                                                            onChange={handleInputChange}
                                                        />
                                                        <label className="custom-control-label" htmlFor="check1">Inglés Requerido</label>
                                                    </div>
                                                </div>
                                                <div className="col-lg-12 col-md-12">
                                                    <div className="form-group">
                                                        <label>Descripción</label>
                                                        <JoditEditor
                                                            ref={editorRef}
                                                            value={descripcionContent}
                                                            config={joditConfig}
                                                            onChange={(newContent) => {
                                                                setDescripcionContent(newContent);
                                                            }}
                                                            onBlur={(newContent) => {
                                                                setDescripcionContent(newContent);
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    descripcion: newContent
                                                                }));
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Modalidad de Trabajo *</label>
                                                        <Form.Control
                                                            as="select"
                                                            className="custom-select"
                                                            name="modalidadTrabajoId"
                                                            value={formData.modalidadTrabajoId}
                                                            onChange={handleInputChange}
                                                            required
                                                        >
                                                            <option value="">Seleccione una opción</option>
                                                            {selectors.modalidadesTrabajo.map((modalidad) => (
                                                                <option key={modalidad.id} value={modalidad.id}>
                                                                    {modalidad.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Tipo de Trabajo *</label>
                                                        <Form.Control
                                                            as="select"
                                                            className="custom-select"
                                                            name="tipoTrabajoId"
                                                            value={formData.tipoTrabajoId}
                                                            onChange={handleInputChange}
                                                            required
                                                        >
                                                            <option value="">Seleccione una opción</option>
                                                            {selectors.tiposTrabajo.map((tipo) => (
                                                                <option key={tipo.id} value={tipo.id}>
                                                                    {tipo.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Tipo de Contrato de Trabajo *</label>
                                                        <Form.Control
                                                            as="select"
                                                            className="custom-select"
                                                            name="tipoContratoTrabajoId"
                                                            value={formData.tipoContratoTrabajoId}
                                                            onChange={handleInputChange}
                                                            required
                                                        >
                                                            <option value="">Seleccione una opción</option>
                                                            {selectors.tiposContrato.map((tipo) => (
                                                                <option key={tipo.id} value={tipo.id}>
                                                                    {tipo.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Tiempo de Experiencia Requerido *</label>
                                                        <Form.Control
                                                            as="select"
                                                            className="custom-select"
                                                            name="tiempoExperienciaRequeridoId"
                                                            value={formData.tiempoExperienciaRequeridoId}
                                                            onChange={handleInputChange}
                                                            required
                                                        >
                                                            <option value="">Seleccione una opción</option>
                                                            {selectors.tiemposExperiencia.map((tiempo) => (
                                                                <option key={tiempo.id} value={tiempo.id}>
                                                                    {tiempo.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Categoría *</label>
                                                        <Form.Control
                                                            as="select"
                                                            className="custom-select"
                                                            name="categoriaId"
                                                            value={formData.categoriaId}
                                                            onChange={handleInputChange}
                                                            required
                                                        >
                                                            <option value="">Seleccione una opción</option>
                                                            {selectors.categorias.map((categoria) => (
                                                                <option key={categoria.id} value={categoria.id}>
                                                                    {categoria.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Salario Mínimo</label>
                                                        <NumericFormat
                                                            className='form-control'
                                                            prefix='$'
                                                            thousandSeparator
                                                            placeholder='Introduce el salario mínimo para esta vacante'
                                                            value={formData.salarioMinimo}
                                                            onValueChange={(values) => handleNumericChange('salarioMinimo', values.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Salario Máximo</label>
                                                        <NumericFormat
                                                            className='form-control'
                                                            prefix='$'
                                                            thousandSeparator
                                                            placeholder='Introduce el salario máximo para esta vacante'
                                                            value={formData.salarioMaximo}
                                                            onValueChange={(values) => handleNumericChange('salarioMaximo', values.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>País *</label>
                                                        <Form.Control
                                                            as="select"
                                                            className="custom-select"
                                                            name="paisId"
                                                            value={formData.paisId}
                                                            onChange={handleCountryChange}
                                                            required
                                                        >
                                                            <option value="">Seleccione un país</option>
                                                            {selectors.paises.map((pais) => (
                                                                <option key={pais.id} value={pais.id}>
                                                                    {pais.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Ciudad *</label>
                                                        <Form.Control
                                                            as="select"
                                                            className="custom-select"
                                                            name="ciudadId"
                                                            value={formData.ciudadId}
                                                            onChange={handleInputChange}
                                                            required
                                                            disabled={!formData.paisId}
                                                        >
                                                            <option value="">
                                                                {formData.paisId ? 'Seleccione una ciudad' : 'Primero seleccione un país'}
                                                            </option>
                                                            {availableCities.map((ciudad) => (
                                                                <option key={ciudad.id} value={ciudad.id}>
                                                                    {ciudad.nombre}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="form-group">
                                                        <label>Dirección *</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder='Introduce la dirección del trabajo'
                                                            name="direccion"
                                                            value={formData.direccion}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-md-4 d-flex align-items-center">
                                                    <div className="form-group">
                                                        <label>Tiempo de Vigencia de la Vacante (Días)</label>
                                                        <NumericFormat
                                                            className='form-control'
                                                            suffix=' Dias'
                                                            placeholder='Dias'
                                                            value={formData.tiempoVigencia}
                                                            onValueChange={(values) => setFormData(prev => ({ ...prev, tiempoVigencia: values.value }))}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-md-4">
                                                    <div className="form-group">
                                                        <label>Límite de Aplicantes</label>
                                                        <NumericFormat
                                                            className='form-control'
                                                            allowNegative={false}
                                                            decimalScale={0}
                                                            placeholder='Sin límite'
                                                            value={formData.limiteAplicantes}
                                                            onValueChange={(values) => setFormData(prev => ({ ...prev, limiteAplicantes: values.value }))}
                                                        />
                                                        <small className="form-text text-muted">Opcional. Número máximo de postulantes.</small>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="submit"
                                                className="site-button m-b30"
                                                disabled={loading}
                                            >
                                                {loading ? 'Guardando...' : (isEditMode ? 'Actualizar Vacante' : 'Publicar Vacante')}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
